/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";
import * as sAApi from "Services/SA/SAApi";
import {
  uploadCsvFileSuccess,
  uploadCsvFileFail,
  IUploadCsvFile,
  ISubmitCsvFile,
  submitCsvFileFail,
  submitCsvFileSuccess,
  uploadCsvFileBadRequest,
} from "./UpdateSAProgramStatusAction";
import { IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { IUploadCSVFileDataRequest } from "Interfaces/SA/IUploadCSVFileDataRequest";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";
import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { ISubmitCSVFileDataRequest } from "Interfaces/SA/ISubmitCSVFileDataRequest";

/**
 * Handle calling the api to upload user profile.
 * @param {IUploadCsvFile} action the Update csv file action.
 * @returns {Generator} the profile actions.
 */
function* handleProgramCsvFileUpload(action: IUploadCsvFile): Generator {
  const failedMessage = "Failed to upload the csv file, Please try again.";

  try {
    const { result, timeout }: IApiResult2<IUploadCSVFileDataRequest> | any = yield race({
      result: call(sAApi.uploadProgramStatusCSVFile, action.uploadCsv),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(uploadCsvFileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(uploadCsvFileSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(uploadCsvFileBadRequest(result.parsedBody));
        break;
      default:
        yield put(uploadCsvFileFail(failedMessage));
    }
  } catch (error) {
    yield put(uploadCsvFileFail(failedMessage));
  }
}

/**
 * Handle calling the api to submit csv file.
 * @param {ISubmitCsvFile} action the Update csv file action.
 * @returns {Generator} the profile actions.
 */
function* handleProgramCsvFileSubmit(action: ISubmitCsvFile): Generator {
  const failedMessage = "Failed to upload the csv file, Please try again.";

  try {
    const { result, timeout }: IApiResult2<ISubmitCSVFileDataRequest> | any = yield race({
      result: call(sAApi.submitProgramStatusCSVFile, action.fileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(submitCsvFileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(submitCsvFileSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(submitCsvFileFail(failedMessage));
        break;
      default:
        yield put(submitCsvFileFail(failedMessage));
    }
  } catch (error) {
    yield put(submitCsvFileFail(failedMessage));
  }
}

export { handleProgramCsvFileUpload, handleProgramCsvFileSubmit };
